import { useEffect, useRef, useState, type RefObject } from 'react';
import { Smile } from 'lucide-react';
import { fetchEmotes, usableEmotes, type Emote } from '../utils/emotes';

/**
 * Küçük bir "emoji seç" açılır ızgarası. Tıklanan emote'un `:slug:` kısayolunu
 * imlecin bulunduğu konuma ekler (metnin sonuna değil).
 */
export function EmojiPickerButton({
  textareaRef,
  value,
  onChange,
  className,
}: {
  textareaRef: RefObject<HTMLTextAreaElement | HTMLInputElement | null>;
  value: string;
  onChange: (next: string) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [emotes, setEmotes] = useState<Emote[]>([]);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && emotes.length === 0) fetchEmotes().then(setEmotes);
  }, [open, emotes.length]);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const list = usableEmotes(emotes);

  function insert(slug: string) {
    const el = textareaRef.current;
    const shortcode = `:${slug}:`;
    if (!el) {
      onChange(value + shortcode);
      setOpen(false);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + shortcode + value.slice(end);
    onChange(next);
    setOpen(false);
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + shortcode.length;
      el.setSelectionRange(caret, caret);
    });
  }

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={className ?? 'p-1.5 text-text-muted hover:text-accent-600 rounded-lg transition-colors'}
        title="Emoji ekle"
      >
        <Smile size={16} />
      </button>
      {open && (
        <div
          ref={popRef}
          className="absolute bottom-full right-0 mb-2 z-20 w-64 max-h-56 overflow-y-auto rounded-xl border border-border bg-surface p-2 shadow-lg grid grid-cols-6 gap-1"
        >
          {list.length === 0 ? (
            <p className="col-span-6 text-xs text-text-muted p-2">Henüz emote yüklenmedi.</p>
          ) : (
            list.map(e => (
              <button
                key={e.id}
                type="button"
                onClick={() => insert(e.slug)}
                title={`:${e.slug}:`}
                className="p-1 rounded-lg hover:bg-accent-500/10 transition-colors flex items-center justify-center"
              >
                <img src={e.image_url!} alt={e.name} className="h-6 w-6" loading="lazy" />
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
